import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Dog, Cat, Bird, Fish, Home, Moon, Sun, Heart, Pill, Salad, Activity, AlertCircle, IndianRupee, Users, Stethoscope, Scissors, Target, Car, Calendar, Clock, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Icon mapping function - replaces emojis with proper SVG icons
const getIcon = (iconName) => {
  const iconMap = {
    '🐕': <Dog className="w-full h-full" />,
    '🐱': <Cat className="w-full h-full" />,
    '🐦': <Bird className="w-full h-full" />,
    '🐰': <Dog className="w-full h-full" />,
    '🐠': <Fish className="w-full h-full" />,
    '🦎': <Activity className="w-full h-full" />,
    '🐾': <Dog className="w-full h-full" />,
    '❤️': <Heart className="w-full h-full" />,
    '🏠': <Home className="w-full h-full" />,
    '🏡': <Home className="w-full h-full" />,
    '🚶': <Dog className="w-full h-full" />,
    '👋': <Users className="w-full h-full" />,
    '🌙': <Moon className="w-full h-full" />,
    '☀️': <Sun className="w-full h-full" />,
    '💊': <Pill className="w-full h-full" />,
    '🥗': <Salad className="w-full h-full" />,
    '🏃': <Activity className="w-full h-full" />,
    '⚠️': <AlertCircle className="w-full h-full" />,
    '✅': <Check className="w-full h-full" />,
    '💰': <IndianRupee className="w-full h-full" />,
    '💰💰': <><IndianRupee className="w-full h-full" /><IndianRupee className="w-full h-full" /></>,
    '💰💰💰': <><IndianRupee className="w-full h-full" /><IndianRupee className="w-full h-full" /><IndianRupee className="w-full h-full" /></>,
    '💎': <IndianRupee className="w-full h-full" />,
    '👨‍⚕️': <Stethoscope className="w-full h-full" />,
    '🤝': <Users className="w-full h-full" />,
    '🩺': <Stethoscope className="w-full h-full" />,
    '✂️': <Scissors className="w-full h-full" />,
    '🎯': <Target className="w-full h-full" />,
    '🚑': <Stethoscope className="w-full h-full" />,
    '🧠': <Activity className="w-full h-full" />,
    '🍼': <Heart className="w-full h-full" />,
    '👥': <Users className="w-full h-full" />,
    '🚗': <Car className="w-full h-full" />,
    '📅': <Calendar className="w-full h-full" />,
    '🎉': <Activity className="w-full h-full" />,
    '🌅': <Sun className="w-full h-full" />,
    '🌆': <Clock className="w-full h-full" />,
    '🎊': <Activity className="w-full h-full" />,
    '🚨': <AlertCircle className="w-full h-full" />,
    '⚡': <Zap className="w-full h-full" />,
    '💪': <Activity className="w-full h-full" />,
    '👴': <Clock className="w-full h-full" />
  };
  
  return iconMap[iconName] || iconName;
};

const OnboardingModal = ({ isOpen, onClose, userRole }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [selectedRole, setSelectedRole] = useState(userRole || null);

  // Role Selection Question (for non-logged users)
  const roleSelectionQuestion = {
    id: 'user_role',
    title: 'What brings you to PetsFostering?',
    subtitle: 'Choose your role to get personalized questions',
    type: 'single',
    options: [
      { 
        id: 'owner', 
        label: 'I need someone to care for my pets', 
        icon: '🐕', 
        description: 'Find trusted caregivers for your beloved pets'
      },
      { 
        id: 'caregiver', 
        label: 'I want to provide pet care services', 
        icon: '❤️', 
        description: 'Connect with pet parents and earn money caring for pets'
      }
    ]
  };

  // Pet Owner Onboarding Questions
  const petOwnerQuestions = [
    {
      id: 'pets',
      title: 'Tell us about your pets',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'dog', label: 'Dog', icon: '🐕' },
        { id: 'cat', label: 'Cat', icon: '🐱' },
        { id: 'bird', label: 'Bird', icon: '🐦' },
        { id: 'rabbit', label: 'Rabbit', icon: '🐰' },
        { id: 'fish', label: 'Fish', icon: '🐠' },
        { id: 'reptile', label: 'Reptile', icon: '🦎' },
        { id: 'other', label: 'Other', icon: '🐾' }
      ]
    },
    {
      id: 'pet_ages',
      title: 'What are the ages of your pets?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'puppy_kitten', label: 'Puppy/Kitten (0-1 year)', icon: '🍼' },
        { id: 'young', label: 'Young (1-3 years)', icon: '⚡' },
        { id: 'adult', label: 'Adult (3-7 years)', icon: '💪' },
        { id: 'senior', label: 'Senior (7+ years)', icon: '👴' }
      ]
    },
    {
      id: 'care_needs',
      title: 'What type of care do you need?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'pet_sitting', label: 'Pet Sitting (At caregiver\'s home)', icon: '🏠' },
        { id: 'house_sitting', label: 'House Sitting (At your home)', icon: '🏡' },
        { id: 'dog_walking', label: 'Dog Walking', icon: '🚶' },
        { id: 'drop_in_visits', label: 'Drop-in Visits', icon: '👋' },
        { id: 'overnight_care', label: 'Overnight Care', icon: '🌙' },
        { id: 'daycare', label: 'Pet Daycare', icon: '☀️' }
      ]
    },
    {
      id: 'special_requirements',
      title: 'Any special requirements?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'medication', label: 'Medication Administration', icon: '💊' },
        { id: 'special_diet', label: 'Special Diet', icon: '🥗' },
        { id: 'exercise_needs', label: 'High Exercise Needs', icon: '🏃' },
        { id: 'behavioral_issues', label: 'Behavioral Considerations', icon: '⚠️' },
        { id: 'elderly_care', label: 'Elderly Pet Care', icon: '❤️' },
        { id: 'none', label: 'No Special Requirements', icon: '✅' }
      ]
    },
    {
      id: 'budget_range',
      title: 'What\'s your budget range?',
      subtitle: 'Select your preferred range',
      type: 'single',
      options: [
        { id: 'budget_low', label: '₹200-500 per day', icon: '💰' },
        { id: 'budget_mid', label: '₹500-1000 per day', icon: '💰💰' },
        { id: 'budget_high', label: '₹1000-2000 per day', icon: '💰💰💰' },
        { id: 'budget_premium', label: '₹2000+ per day', icon: '💎' }
      ]
    }
  ];

  // Caregiver Onboarding Questions
  const caregiverQuestions = [
    {
      id: 'experience',
      title: 'What\'s your experience with pets?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'owner', label: 'Pet Owner', icon: '🏠' },
        { id: 'professional', label: 'Professional Pet Care', icon: '👨‍⚕️' },
        { id: 'volunteer', label: 'Volunteer at Shelter', icon: '🤝' },
        { id: 'vet_tech', label: 'Veterinary Background', icon: '🩺' },
        { id: 'groomer', label: 'Pet Grooming', icon: '✂️' },
        { id: 'trainer', label: 'Pet Training', icon: '🎯' }
      ]
    },
    {
      id: 'pet_types',
      title: 'Which pets are you comfortable caring for?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'dogs', label: 'Dogs', icon: '🐕' },
        { id: 'cats', label: 'Cats', icon: '🐱' },
        { id: 'birds', label: 'Birds', icon: '🐦' },
        { id: 'rabbits', label: 'Rabbits', icon: '🐰' },
        { id: 'fish', label: 'Fish', icon: '🐠' },
        { id: 'reptiles', label: 'Reptiles', icon: '🦎' },
        { id: 'exotic', label: 'Exotic Pets', icon: '🐾' }
      ]
    },
    {
      id: 'services',
      title: 'What services can you provide?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'pet_sitting', label: 'Pet Sitting (At your home)', icon: '🏠' },
        { id: 'house_sitting', label: 'House Sitting (At owner\'s home)', icon: '🏡' },
        { id: 'dog_walking', label: 'Dog Walking', icon: '🚶' },
        { id: 'drop_in_visits', label: 'Drop-in Visits', icon: '👋' },
        { id: 'overnight_care', label: 'Overnight Care', icon: '🌙' },
        { id: 'pet_taxi', label: 'Pet Transportation', icon: '🚗' },
        { id: 'grooming', label: 'Basic Grooming', icon: '✂️' },
        { id: 'training', label: 'Basic Training', icon: '🎯' }
      ]
    },
    {
      id: 'special_skills',
      title: 'Do you have any special skills?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'medication', label: 'Medication Administration', icon: '💊' },
        { id: 'first_aid', label: 'Pet First Aid Certified', icon: '🚑' },
        { id: 'behavioral', label: 'Behavioral Issues', icon: '🧠' },
        { id: 'senior_care', label: 'Senior Pet Care', icon: '❤️' },
        { id: 'puppy_care', label: 'Puppy/Kitten Care', icon: '🍼' },
        { id: 'multiple_pets', label: 'Multiple Pet Households', icon: '👥' },
        { id: 'none', label: 'No Special Skills', icon: '✅' }
      ]
    },
    {
      id: 'availability',
      title: 'When are you typically available?',
      subtitle: 'Select all that apply',
      type: 'multiselect',
      options: [
        { id: 'weekdays', label: 'Weekdays', icon: '📅' },
        { id: 'weekends', label: 'Weekends', icon: '🎉' },
        { id: 'mornings', label: 'Mornings', icon: '🌅' },
        { id: 'afternoons', label: 'Afternoons', icon: '☀️' },
        { id: 'evenings', label: 'Evenings', icon: '🌆' },
        { id: 'overnight', label: 'Overnight', icon: '🌙' },
        { id: 'holidays', label: 'Holidays', icon: '🎊' },
        { id: 'emergency', label: 'Emergency Situations', icon: '🚨' }
      ]
    }
  ];

  // Determine questions based on selected role
  const getQuestions = () => {
    if (!user && !selectedRole) {
      return [roleSelectionQuestion];
    }
    const role = selectedRole || userRole;
    return role === 'owner' ? petOwnerQuestions : caregiverQuestions;
  };
  
  const questions = getQuestions();

  const handleOptionToggle = (questionId, optionId) => {
    const question = questions[currentStep];
    
    // Handle role selection for non-logged users
    if (questionId === 'user_role') {
      setSelectedRole(optionId);
      setResponses(prev => ({
        ...prev,
        [questionId]: [optionId]
      }));
      // Auto-advance after role selection
      setTimeout(() => {
        if (currentStep < questions.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      }, 300);
      return;
    }
    
    if (question.type === 'single') {
      setResponses(prev => ({
        ...prev,
        [questionId]: [optionId]
      }));
    } else {
      setResponses(prev => {
        const currentSelections = prev[questionId] || [];
        const isSelected = currentSelections.includes(optionId);
        
        if (isSelected) {
          return {
            ...prev,
            [questionId]: currentSelections.filter(id => id !== optionId)
          };
        } else {
          return {
            ...prev,
            [questionId]: [...currentSelections, optionId]
          };
        }
      });
    }
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    // Save onboarding responses to Firestore
    const onboardingData = {
      userId: user?.id || 'anonymous',
      role: selectedRole || userRole,
      responses,
      completedAt: new Date().toISOString()
    };
    
    if (user?.id) {
      try {
        // Import Firestore functions
        const { doc, setDoc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../config/firebase');
        
        // Save to Firestore users collection
        await updateDoc(doc(db, 'users', user.id), {
          onboardingCompleted: true,
          onboardingResponses: responses,
          onboardingCompletedAt: new Date().toISOString(),
          preferences: responses // Store as preferences for easy access
        });
        
        // Also save to localStorage as backup
        localStorage.setItem(`onboarding_${user.id}`, JSON.stringify(onboardingData));
        
        console.log('✅ Onboarding responses saved to Firestore!');
      } catch (error) {
        console.error('Failed to save onboarding to Firestore:', error);
        // Fallback to localStorage only
        localStorage.setItem(`onboarding_${user.id}`, JSON.stringify(onboardingData));
      }
    }
    
    onClose(onboardingData);
  };

  const handleClose = () => {
    // Reset state when closing
    setCurrentStep(0);
    setResponses({});
    setSelectedRole(userRole || null);
    onClose();
  };

  const currentQuestion = questions[currentStep];
  const currentSelections = responses[currentQuestion?.id] || [];
  const canProceed = currentSelections.length > 0;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-4 pf-modal-backdrop"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-xl md:rounded-2xl max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-hidden pf-modal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Compact on Mobile */}
        <div className="bg-[var(--pf-accent)] text-white p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h2 className="text-lg md:text-2xl font-bold">Welcome to PetsFostering!</h2>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="text-white hover:text-gray-200 transition-colors p-1 z-10"
            >
              <X className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-white bg-opacity-20 rounded-full h-1.5 md:h-2">
            <div
              className="bg-white h-1.5 md:h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
            />
          </div>
          <div className="text-xs md:text-sm mt-2 opacity-90">
            Step {currentStep + 1} of {questions.length}
          </div>
        </div>

        {/* Content with Fade Transition */}
        <div className="p-4 md:p-6 flex-1 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 180px)' }}>
          <div key={currentStep} className="text-center mb-4 md:mb-8 pf-content-enter">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              {currentQuestion.title}
            </h3>
            <p className="text-sm md:text-base text-gray-600">{currentQuestion.subtitle}</p>
          </div>

          <div key={`${currentStep}-options`} className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-8 pf-stagger-options">
            {currentQuestion.options.map((option, index) => {
              const isSelected = currentSelections.includes(option.id);
              const isRoleSelection = currentQuestion.id === 'user_role';
              return (
                <button
                  key={option.id}
                  onClick={() => handleOptionToggle(currentQuestion.id, option.id)}
                  className={`p-3 md:p-4 rounded-lg md:rounded-xl border-2 text-left relative transition-[transform,border-color,background-color,box-shadow] duration-[160ms] ease-out active:scale-[0.98] ${
                    isSelected
                      ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] shadow-md'
                      : 'border-[var(--pf-border)] hover:border-[var(--pf-accent)]'
                  } ${isRoleSelection ? 'min-h-[100px] md:min-h-[120px]' : ''}`}
                >
                  <div className="flex items-start gap-2 md:gap-3">
                    <div className={`w-8 h-8 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center rounded-lg ${
                      isSelected ? 'bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getIcon(option.icon)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm md:text-lg mb-0.5 md:mb-1 pr-6">{option.label}</div>
                      {option.description && (
                        <div className="text-xs md:text-sm text-gray-600 line-clamp-2">{option.description}</div>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-[var(--pf-accent)] absolute top-2 right-2 md:top-3 md:right-3" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer - Compact on Mobile */}
        <div className="border-t border-gray-200 p-3 md:p-6 bg-gray-50">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 text-sm md:text-base text-gray-600 hover:text-gray-800 disabled:opacity-0 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            {currentStep === questions.length - 1 ? (
              <button
                onClick={handleComplete}
                disabled={!canProceed}
                className="bg-[var(--pf-accent)] text-white px-5 md:px-8 py-2.5 md:py-3 rounded-lg font-semibold transition-[transform,box-shadow] duration-[160ms] ease-out active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base flex-1 max-w-xs"
              >
                Complete Setup
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed}
                className="flex items-center justify-center gap-1 md:gap-2 bg-[var(--pf-accent)] text-white px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold transition-[transform,background-color] duration-[160ms] ease-out active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base flex-1 max-w-xs"
              >
                Next
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
